import React, { ReactNode, useCallback, useState } from "react";

import { faMicrochip } from "@fortawesome/pro-regular-svg-icons";
import { AxiosError, isAxiosError } from "axios";
import { omit, pick } from "lodash-es";

import { Flow } from "src/api/flowTypes";
import { BackendErrorData } from "src/api/queries";
import { ErrorBaseInfo, ParsedModelDataError } from "src/api/types";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { MLNode, MLNodeData } from "src/constants/NodeDataTypes";
import { Accordion } from "src/design-system/Accordion";
import { Callout } from "src/design-system/Callout";
import { Icon } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { DataContainer } from "src/mlNode/DataContainer";
import { MetadataField } from "src/mlNode/MetadataField";
import { ModelDropzone } from "src/mlNode/ModelDropzone";
import { ModelFile } from "src/mlNode/ModelFile";
import { OverwriteConfirmationModal } from "src/mlNode/OverwriteConfirmationModal";
import { useUploadModel } from "src/mlNode/queries";
import { Tensor } from "src/mlNode/Tensor";
import { useFormApplyNodeSuggestion } from "src/nodeEditor/applyNodeSuggestion";
import { CodeNodeEditor } from "src/nodeEditor/CodeNodeEditor";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";

const ContentContainer: React.FC<{
  children: ReactNode;
  error?: AxiosError<BackendErrorData> | ParsedModelDataError["error"] | null;
}> = ({ children, error }) => (
  <div className="flex h-full flex-col">
    {error && (
      <div className="mb-4">
        <Callout variant="error">
          {isAxiosError(error)
            ? (error.response?.data?.detail ?? "Unknown error")
            : error.error}
        </Callout>
      </div>
    )}
    {children}
  </div>
);

type Props = {
  selectedNode: MLNode;
  flow: Flow;
  workspace: WorkspaceDataplane;
  displayedError?: ErrorBaseInfo;
} & NodeEditorBaseProps<MLNodeData>;

export const MLNodeEditor: React.FC<Props> = ({
  selectedNode,
  flow,
  workspace,
  immutable,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const { renderedInDiffView } = useDiffViewContext();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const isCodeSearchHighlighted = useIsFieldHighlighted(
    `${selectedNode.id}:src`,
  );

  const updateNode = useCallback(
    (data: Partial<MLNodeData>) => {
      onUpdate({ newData: data });
    },
    [onUpdate],
  );

  useFormApplyNodeSuggestion<{ src: string }>(selectedNode.id, (suggestion) => {
    if (suggestion.src) {
      updateNode({ src: suggestion.src });
    }
  });

  const uploadModel = useUploadModel({
    baseUrl: workspace.base_url,
    flowSlug: flow.slug,
    onMutate: (modelData) => {
      updateNode({ model_filename: modelData.file.name });
      // Keep old file name in context object
      return { oldFileName: selectedNode.data?.model_filename };
    },
    onSuccess: (data) => {
      if (selectedNode.data?.model_id) {
        updateNode(omit(data.metadata, ["src", "model_filename"]));
        setIsOpenModal(true);
      } else {
        updateNode(omit(data.metadata, ["model_filename"]));
      }
    },
    onError: (context) => {
      // Rollback to the old file name
      updateNode({ model_filename: context?.oldFileName });
      toastActions.failure({ title: "An error happened" });
    },
  });

  const handleFileSelect = (file: File) => {
    if (!workspace.base_url) return;

    uploadModel.onFileSelect(file);

    toastActions.default({ title: "Model uploading started" });
  };

  if (selectedNode.data.model_id === "") {
    if (!immutable) {
      return (
        <ContentContainer error={uploadModel.error}>
          <ModelDropzone
            isUploading={uploadModel.isLoading}
            workspace={workspace}
            onFileSelect={handleFileSelect}
          />
        </ContentContainer>
      );
    } else {
      return (
        <div className="relative flex h-full grow flex-col items-center justify-center rounded-xl border border-gray-200 bg-gray-50">
          <div className="flex items-center justify-center rounded-full border-2 border-indigo-200 p-1.5">
            <Icon variant="active" icon={faMicrochip} size="md" />
          </div>
          <p className="mt-5 select-none font-inter-semibold-13px text-gray-800">
            No Model File Uploaded
          </p>
        </div>
      );
    }
  }

  const { inputs, outputs, custom_metadata, ...nodedata } = selectedNode.data;
  const metadata = {
    ...pick(nodedata, [
      "description",
      "graph_name",
      "producer_name",
      "version",
    ]),
    ...custom_metadata,
  };
  return (
    <ContentContainer error={uploadModel.error}>
      <ModelFile
        isUploading={uploadModel.isLoading}
        modelName={selectedNode.data.model_filename}
        workspace={workspace}
        onFileSelect={handleFileSelect}
      />
      <Accordion
        defaultValue={
          renderedInDiffView
            ? ["metadata", "inputs", "outputs", "code"]
            : ["metadata", isCodeSearchHighlighted ? "code" : ""]
        }
      >
        <Accordion.Item
          isReadOnly={renderedInDiffView}
          label="Model metadata"
          value="metadata"
        >
          <DataContainer>
            {Object.entries(metadata).map(([name, value], index) => (
              <MetadataField
                key={name}
                dataLoc={`metadata-${index}`}
                name={name}
                value={value}
              />
            ))}
          </DataContainer>
        </Accordion.Item>
        <Accordion.Item
          isReadOnly={renderedInDiffView}
          label="Model inputs"
          value="inputs"
        >
          <DataContainer>
            {inputs?.map((tensor, index) => (
              <Tensor
                key={tensor.name}
                dataLoc={`inputs-${index}`}
                {...tensor}
              />
            ))}
          </DataContainer>
        </Accordion.Item>
        <Accordion.Item
          isReadOnly={renderedInDiffView}
          label="Model outputs"
          value="outputs"
        >
          <DataContainer>
            {outputs?.map((tensor, index) => (
              <Tensor
                key={tensor.name}
                dataLoc={`outputs-${index}`}
                {...tensor}
              />
            ))}
          </DataContainer>
        </Accordion.Item>
        <Accordion.Item
          isReadOnly={renderedInDiffView}
          label="Code"
          value="code"
        >
          <div className="h-128">
            <CodeNodeEditor
              displayedError={displayedError}
              immutable={immutable}
              isReactive={isReactive}
              language="python"
              nodeId={selectedNode.id}
              src={selectedNode.data?.src}
              onChange={(value) => updateNode({ src: value })}
            />
          </div>
        </Accordion.Item>
      </Accordion>
      <OverwriteConfirmationModal
        isOpen={isOpenModal}
        onCancel={() => setIsOpenModal(false)}
        onConfirm={() => {
          updateNode({ src: uploadModel.data?.metadata.src });
          setIsOpenModal(false);
        }}
      />
    </ContentContainer>
  );
};
